import React, { useState, useEffect } from "react";
import { Container, Grid, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import * as caseStudyService from "../../services/CasestudyService";
import CaseStudyCard from "./components/CasestudyCard";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import Notification from "../../../../components/mui/Notification";
import PageHeader from "../../../../components/PageHeader";
import { formatDate } from "../../../../helper/DateHelper";
import {
  CASE_STUDY_ASSIGNMENTS,
  CASE_STUDY_MINI_NEW,
  CASE_STUDY_MINI_REVIEW,
  CASE_STUDY_NEW,
  CASE_STUDY_PAYMENT_OPTION_ROUTE,
  CASE_STUDY_STANDARD_REVIEW,
  INSTRUCTOR_COURSE_LIST_ROUTE,
} from "../../helper/Constants";
import { TourRide, TourStep } from "../../../../components";
import NewCaseStudyCard from "./components/NewCaseStudyCard";

export default function List() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [refresh, setRefresh] = useState();

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(true);
  const [caseStudies, setCaseStudies] = useState([]);
  const course = state.course;
  const institutionId = state.institutionId;

  const onRefresh = () => {
    setRefresh(!refresh);
  };

  const tourSteps = [
    {
      target: "#new-card",
      content: (
        <TourStep
          title={"Create Case Study"}
          description={"Start creating a new case study."}
        />
      ),
    },
    {
      target: "#download-case-study",
      content: (
        <TourStep
          title={"Download Case Study"}
          description={"You can download case study file here."}
        />
      ),
    },
    {
      target: "#create-teaching-note",
      content: (
        <TourStep
          title={"Teaching Note"}
          description={
            "You can request or access teaching note file here. Please note that teaching note creation might take several minutes."
          }
        />
      ),
    },
    {
      target: "#page-header-refresh",
      content: (
        <TourStep
          title={"Refresh the page"}
          description={
            "Use this button to refresh the page after creating a teaching note."
          }
        />
      ),
    },
  ];

  useEffect(() => {
    // if (!isEpmty(state)) {
    // const eventId = state.eventId;
    // setEventId(eventId);
    caseStudyService
      .getAll(course.id)
      .then((response) => {
        setCaseStudies(response.data);
      })
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        setNotify({
          isOpen: true,
          message: "Could not connect to server. Please try again later.",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
    // } else setLoading(false);
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssignments = (casestudy) => {
    navigate(CASE_STUDY_ASSIGNMENTS, {
      state: { casestudy, institutionId, course },
    });
  };

  const handleStandardCase = (casestudy) => {
    navigate(CASE_STUDY_STANDARD_REVIEW, {
      state: { casestudy, institutionId, course },
    });
  };

  const handleMiniCase = (casestudy) => {
    navigate(CASE_STUDY_MINI_REVIEW, {
      state: { casestudy, institutionId, course },
    });
  };

  const onDelete = (id) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false,
    });
    caseStudyService
      .del(id)
      .then(() => {
        const newCases = caseStudies.filter((item) => item.id !== id);
        setCaseStudies(newCases);
        setNotify({
          isOpen: true,
          message: "Deleted Successfully",
          type: "success",
        });
      })
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        setNotify({
          isOpen: true,
          message: "Could not delete a case study. Please try again later.",
          type: "error",
        });
      });
  };

  const handleView = (caseStudy) => {
    navigate("/casestudy/view", {
      state: { course, caseStudy },
    });
  };

  const handleTeachingNoteCreation = (caseStudy, course) => {
    const request = {
      courseId: course.id,
      caseStudyId: caseStudy.id,
      caseStudyType: caseStudy.type,
    };
    caseStudyService
      .createTeachingNote(request)
      .then((response) => {})
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        if (e.response && e.response.status === 409) {
          setNotify({
            isOpen: true,
            message:
              "Teaching notes creation still in progress. Please try again later.",
            type: "info",
          });
        } else {
          setNotify({
            isOpen: true,
            message:
              "Could not create a case study teaching note. Please try again later.",
            type: "error",
          });
        }
      });
  };

  const handleTeachingNotes = (caseStudy, course) => {
    caseStudyService
      .getTeachingNotes(caseStudy.id)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "Teaching_Notes_" +
            course.code +
            "_" +
            course.section +
            "_" +
            formatDate(new Date()) +
            ".pdf"
        );
        document.body.appendChild(link);
        link.click();
      })
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        if (e.response && e.response.status === 404) {
          setNotify({
            isOpen: true,
            message:
              "Teaching notes creation still in progress. Please try again later.",
            type: "info",
          });
        } else {
          setNotify({
            isOpen: true,
            message: "Could not connect to server. Please try again later.",
            type: "error",
          });
        }
      });
  };

  const handlePayment = (casestudy) => {
    navigate(CASE_STUDY_PAYMENT_OPTION_ROUTE, {
      state: { casestudy, institutionId, course },
    });
  };

  const handlePdf = (caseStudy, course) =>
    caseStudyService
      .getPdf(caseStudy.id, caseStudy.paymentStatus, caseStudy.type)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "Case_study_" +
            course.code +
            "_" +
            course.section +
            "_" +
            formatDate(new Date()) +
            ".pdf"
        );
        document.body.appendChild(link);
        link.click();
      })
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        if (e.response && e.response.status === 404) {
          setNotify({
            isOpen: true,
            message: "Could not find a pdf. Please try again later.",
            type: "warning",
          });
        } else if (e.response && e.response.status === 402) {
          setNotify({
            isOpen: true,
            message:
              "Download limit exceeded the payment quantity. Please contact us for further instruction.",
            type: "error",
          });
        } else {
          setNotify({
            isOpen: true,
            message: "Could not connect to server. Please try again later.",
            type: "error",
          });
        }
      });

  const handleEdit = (caseStudy) => {
    navigate("/casestudy/edit", { state: { course, caseStudy } });
  };

  const handleNew = () => {
    navigate(CASE_STUDY_NEW, { state: { course } });
  };

  const handleNewMini = () => {
    navigate(CASE_STUDY_MINI_NEW, { state: { course } });
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure to delete this case study?",
      subTitle: "You can't undo this operation",
      onConfirm: () => {
        onDelete(id);
      },
    });
  };

  const handleBackButton = () => {
    navigate(INSTRUCTOR_COURSE_LIST_ROUTE, {
      state: { institutionId },
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {caseStudies.length > 0 && (
        <TourRide name="casestudy-list-page" steps={tourSteps} />
      )}
      <PageHeader
        title="List of Case Studies"
        isChild={true}
        onRefresh={() => onRefresh()}
        backHandler={() => handleBackButton()}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <NewCaseStudyCard
            title={"New Case Study"}
            handleStandard={handleNew}
            handleMini={handleNewMini}
          />
        </Grid>
        {loading
          ? Array.from(new Array(3)).map((item, index) => (
              <Grid
                key={index}
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                sx={{ mt: 2 }}
              >
                <Skeleton variant="rectangular" height={118} />
                <Skeleton />
                <Skeleton width="60%" />
              </Grid>
            ))
          : caseStudies.map((caseStudy, index) => (
              <Grid key={index} item xs={12} sm={6} md={4}>
                <CaseStudyCard
                  refresh={() => onRefresh()}
                  caseStudy={caseStudy}
                  handleEdit={() => handleEdit(caseStudy)}
                  handleView={() => handleView(caseStudy)}
                  handleDelete={() => handleDelete(caseStudy.id)}
                  handleTeachingNotes={() =>
                    handleTeachingNotes(caseStudy, course)
                  }
                  createTeachingNote={() =>
                    handleTeachingNoteCreation(caseStudy, course)
                  }
                  handlePdf={() => handlePdf(caseStudy, course)}
                  handlePayment={() => handlePayment(caseStudy)}
                  handleAssignments={() => handleAssignments(caseStudy)}
                  handleStandardCase={() => handleStandardCase(caseStudy)}
                  handleMiniCase={() => handleMiniCase(caseStudy)}
                />
              </Grid>
            ))}
      </Grid>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Container>
  );
}
